import Image from 'next/image';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonPrimary,
  buttonSecondary,
  CheckGlyph,
  delay,
  tone,
} from '@/components/agents/primitives';
import LandingPageNavigation from '@/components/layout/LandingPageNavigation';

import { WORKSHOP_PARTNERS } from '@/constants/workshops';
import { scrollToSection, WORKSHOP_CONTACT_ID } from '@/utils/scrollToSection';

import { Brand, PHOTOS } from './kit';

const TRAINERS = [
  {
    name: 'Marianna',
    avatar:
      'https://media.easy-peasy.ai/landing/workshops/trainers/marianna.webp',
  },
  {
    name: 'Dmytro',
    avatar:
      'https://media.easy-peasy.ai/landing/workshops/trainers/dmytro.webp',
  },
];

const PROMISES = [
  'No AI experience needed',
  'Bring a laptop',
  'Slides and guide to keep',
];

export default function WorkshopsHero() {
  return (
    <header className='relative'>
      <LandingPageNavigation />

      <div className='mx-auto grid max-w-7xl gap-x-14 gap-y-14 px-6 pt-12 sm:pt-20 lg:grid-cols-12 lg:items-center lg:px-8'>
        <div className='lg:col-span-6'>
          <a
            href='#how-it-works'
            onClick={(event) => scrollToSection(event, 'how-it-works')}
            className={clsxm(
              styles.enter,
              'group inline-flex max-w-full items-center gap-2.5 rounded-full bg-white py-1 pe-3.5 ps-1 text-sm font-medium shadow-[0_1px_2px_rgba(15,15,40,0.05)] ring-1 ring-[#0f0f28]/[0.08] transition hover:ring-[#0f0f28]/20 dark:bg-white/[0.05] dark:ring-white/10 dark:hover:ring-white/25',
              tone.body,
            )}
          >
            <span className='flex-none rounded-full bg-primary-600 px-2 py-0.5 text-xs font-semibold text-white'>
              3 hours
            </span>
            <span className='truncate'>
              In-person AI workshops in Singapore
            </span>
            <ArrowGlyph className='h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5' />
          </a>

          <h1
            className={clsxm(
              styles.display,
              styles.enter,
              'mt-8 text-[2.875rem] font-semibold leading-[1.02] sm:text-7xl sm:leading-[1] lg:text-[5rem]',
              tone.ink,
            )}
            style={delay(60)}
          >
            Make AI work{' '}
            <span className='text-primary-600 dark:text-primary-400'>
              for you.
            </span>
          </h1>

          <div className={styles.enter} style={delay(160)}>
            <p
              className={clsxm(
                'mt-7 max-w-xl text-lg leading-8 sm:text-[1.3125rem] sm:leading-[1.6]',
                tone.body,
              )}
            >
              Hands-on AI workshops for business owners and company teams. In
              three hours you learn <Brand /> by using it: content, websites,
              slides, videos and AI agents, made on your own laptop.
            </p>

            <div className='mt-9 flex flex-wrap items-center gap-3'>
              <a
                href={`#${WORKSHOP_CONTACT_ID}`}
                onClick={scrollToSection}
                className={buttonPrimary}
              >
                Plan a workshop
                <ArrowGlyph />
              </a>
              <a
                href='#curriculum'
                onClick={(event) => scrollToSection(event, 'curriculum')}
                className={buttonSecondary}
              >
                What you&apos;ll learn
              </a>
            </div>

            <ul
              className={clsxm(
                'mt-9 flex flex-wrap gap-x-6 gap-y-2.5 text-[15px] font-medium',
                tone.body,
              )}
            >
              {PROMISES.map((promise) => (
                <li key={promise} className='flex items-center gap-2'>
                  <CheckGlyph className='h-[18px] w-[18px] flex-none' />
                  {promise}
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div
          className={clsxm(styles.enter, 'relative lg:col-span-6')}
          style={delay(240)}
        >
          <figure className='relative aspect-[4/3] overflow-hidden rounded-[28px] bg-[#e8e8f0] shadow-[0_40px_80px_-40px_rgba(30,27,75,0.55)] ring-1 ring-[#0f0f28]/[0.06] sm:rounded-[36px] dark:bg-white/5 dark:ring-white/10'>
            <Image
              src={PHOTOS.hero}
              alt='Business owners at an Easy-Peasy.AI workshop in Singapore'
              fill
              priority
              unoptimized
              className='object-cover object-[50%_45%]'
            />
            <figcaption className='absolute start-4 top-4 rounded-full bg-white/90 px-3.5 py-1.5 text-[13px] font-medium text-[#10101c] shadow-[0_6px_20px_-8px_rgba(15,15,40,0.45)] backdrop-blur sm:start-6 sm:top-6'>
              Our October 2025 workshop
            </figcaption>
          </figure>

          <div
            className={clsxm(
              'absolute -bottom-7 end-4 flex items-center gap-3 rounded-2xl py-3 pe-4 ps-3 shadow-[0_24px_50px_-24px_rgba(30,27,75,0.55)] sm:end-8',
              tone.surface,
              tone.line,
            )}
          >
            <div className='flex -space-x-2.5 rtl:space-x-reverse'>
              {TRAINERS.map(({ name, avatar }) => (
                <Image
                  key={name}
                  src={avatar}
                  alt=''
                  width={40}
                  height={40}
                  unoptimized
                  className='h-10 w-10 rounded-full object-cover ring-2 ring-white dark:ring-[#14141f]'
                />
              ))}
            </div>
            <div className='text-sm leading-5'>
              <p className={clsxm('font-semibold', tone.ink)}>
                Taught by the founders
              </p>
              <p className={tone.quiet}>Marianna and Dmytro</p>
            </div>
          </div>
        </div>
      </div>

      <div className='mx-auto mt-20 max-w-7xl px-6 sm:mt-24 lg:px-8'>
        <div className='flex flex-col gap-4 border-t border-[#0f0f28]/[0.08] pt-8 lg:flex-row lg:items-baseline lg:gap-10 dark:border-white/[0.08]'>
          <p className={clsxm('flex-none text-[13px] font-medium', tone.quiet)}>
            We&apos;ve run sessions with
          </p>
          <ul
            className={clsxm(
              'flex flex-wrap gap-x-7 gap-y-2.5 text-[15px] font-semibold tracking-[-0.01em]',
              tone.ink,
            )}
          >
            {WORKSHOP_PARTNERS.map((partner) => (
              <li key={partner}>{partner}</li>
            ))}
          </ul>
        </div>
      </div>
    </header>
  );
}
